import React, { useContext, useEffect, useState } from 'react'
import { CompanyTag } from '@/components/ui/badge'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { CreateCampaignCountryOverride } from 'api/Trading-Accounts/Types/CreateCampaignCountryOverride'
import {
  CountryOverride,
  MarketingCampaignDetailDto,
} from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { UpdateCampaignCountryOverride } from 'api/Trading-Accounts/Types/UpdateCampaignCountryOverride'
import AddButton from 'components/AddButton/AddButton'
import { Modal } from 'components/CustomModal'
import { FilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'

import { CampaignTab } from './CampaignSlug'
import { AddCountryOverrideForm } from './CountryOverrideForm/AddCountryOverrideForm'
import { EditCountryOverrideForm } from './CountryOverrideForm/EditCountryOverrideForm'
import { CampaignNavigation } from './Navigation/CampaignNavigation'

interface OuterProps {
  data: MarketingCampaignDetailDto

  forceRefresh(): void
}

const useCampaignDetailsCountryExceptionsFetch = (props: OuterProps) => {
  const { data } = props

  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const countries = useAppSelector((state) => state.identity.countries)

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
  }, [data, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  return { tickmillCompanies, countries }
}

const useCampaignDetailsCountryExceptionsMutate = (props: OuterProps) => {
  const { data, forceRefresh } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const setToast = useContext(ToastContext)

  const [isAddCountryOverrideModalOpen, setAddCountryOverrideModalOpen] = useState(false)
  const [showEditCountryOverrideModal, setEditCountryOverrideModal] = useState<CountryOverride>()

  const handleAddSubmit = async (values: CreateCampaignCountryOverride[]) => {
    const campaignId = data.id
    await Promise.all(
      values.map((x) => {
        return tradingAccountsApiClient.addMarketingCampaignCountryOverride(campaignId, x)
      })
    )
    setToast(successToast(`Country Override added successfully.`))
    setAddCountryOverrideModalOpen(false)
    forceRefresh()
  }

  const handleEditSubmit = async (values: UpdateCampaignCountryOverride) => {
    const campaignId = data.id
    const countryOverrideId = values.id
    if (campaignId && countryOverrideId) {
      await tradingAccountsApiClient.editMarketingCampaignCountryOverride(
        campaignId,
        countryOverrideId,
        values
      )
      setToast(successToast(`Country Override edited successfully.`))
      setEditCountryOverrideModal(undefined)
      forceRefresh()
    }
  }

  const handleDelete = async (values: CountryOverride) => {
    const campaignId = data.id
    const countryOverrideId = values.id
    if (campaignId && countryOverrideId) {
      await tradingAccountsApiClient.deleteMarketingCampaignCountryOverride(
        campaignId,
        countryOverrideId
      )
      setToast(successToast(`Country Override deleted successfully.`))
      forceRefresh()
    }
  }

  return {
    isAddCountryOverrideModalOpen,
    setAddCountryOverrideModalOpen,
    showEditCountryOverrideModal,
    setEditCountryOverrideModal,
    handleAddSubmit,
    handleEditSubmit,
    handleDelete,
  }
}

const CampaignDetailsCountryExceptionsPage: React.FC<OuterProps> = (props) => {
  const { data } = props

  const [auth] = useContext(AuthSessionContext)

  const { countries, tickmillCompanies } = useCampaignDetailsCountryExceptionsFetch(props)
  const mutate = useCampaignDetailsCountryExceptionsMutate(props)

  const { isAddCountryOverrideModalOpen, showEditCountryOverrideModal } = mutate
  const permissions = auth?.permissions || []

  return (
    <React.Fragment>
      {isAddCountryOverrideModalOpen && (
        <Modal
          visible={isAddCountryOverrideModalOpen}
          onClose={() => mutate.setAddCountryOverrideModalOpen(false)}
        >
          <AddCountryOverrideForm
            data={data}
            tickmillCompanies={tickmillCompanies}
            countryOverrides={data.countryOverrides}
            countries={countries}
            onSubmit={mutate.handleAddSubmit}
          />
        </Modal>
      )}
      {showEditCountryOverrideModal && (
        <Modal
          visible={!!showEditCountryOverrideModal}
          onClose={() => mutate.setEditCountryOverrideModal(undefined)}
        >
          <EditCountryOverrideForm
            data={data}
            country={showEditCountryOverrideModal}
            tickmillCompanies={tickmillCompanies}
            countries={countries}
            onSubmit={mutate.handleEditSubmit}
          />
        </Modal>
      )}
      <CampaignNavigation data={data} tab={CampaignTab.PARTICIPATING_COUNTRIES} />
      <AddButton
        text='Add Country'
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditMarketingCampaigns)}
        onClick={() => mutate.setAddCountryOverrideModalOpen(true)}
        data-test='add-user-button'
      />
      <Grid
        data={data.countryOverrides.sort(
          (a, b) =>
            stringComparator(a.country.name, b.country.name) ||
            stringComparator(a.tickmillCompany.name, b.tickmillCompany.name)
        )}
        total={data.countryOverrides.length}
        resizable
      >
        <Column field='country.name' title='Country' />
        <Column
          field='minAmount'
          cell={(gridCellProps) => (
            <td className='table-columns__number'>{gridCellProps.dataItem.minAmount.toFixed(2)}</td>
          )}
          title='Minimum Deposit'
        />
        <Column
          minResizableWidth={80}
          width={80}
          field='tickmillCompany.name'
          title='Entity'
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem?.tickmillCompany?.id) {
              return <td />
            }

            return (
              <td>
                <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                  {gridCellProps.dataItem.tickmillCompany.name}
                </CompanyTag>
              </td>
            )
          }}
        />
        <Column
          cell={(gridCellProps) => {
            return (
              <td align='center' style={{ textAlign: 'center' }}>
                <input type='checkbox' checked={gridCellProps.dataItem.enabled} disabled />
              </td>
            )
          }}
          title='Status'
        />
        <Column
          width={50}
          title=''
          filterCell={FilterCell}
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    disabled: !hasPermission(
                      permissions,
                      PermissionsEnum.CanEditMarketingCampaigns
                    ),
                    onClick: () => mutate.setEditCountryOverrideModal(gridCellProps.dataItem),
                  },
                  {
                    name: 'Delete',
                    disabled: !hasPermission(
                      permissions,
                      PermissionsEnum.CanEditMarketingCampaigns
                    ),
                    onClick: () => mutate.handleDelete(gridCellProps.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
    </React.Fragment>
  )
}

export default CampaignDetailsCountryExceptionsPage
